import pluralizeLib from 'pluralize';

/**
 * Utility functions for string manipulation
 */

/**
 * Returns a count with the properly pluralized word.
 * Handles irregular plurals (goose → geese, child → children, etc.)
 *
 * @param count - The number of items
 * @param word - The singular form of the word
 * @param inclusive - Whether to include the count in the output (default: true)
 * @returns The pluralized string with count
 *
 * @example
 * pluralize(1, 'document') // '1 document'
 * pluralize(9, 'document') // '9 documents'
 * pluralize(1, 'goose') // '1 goose'
 * pluralize(2, 'goose') // '2 geese'
 * pluralize(5, 'child') // '5 children'
 * pluralize(3, 'document', false) // 'documents'
 */
export function pluralize(count: number, word: string, inclusive: boolean = true): string {
   return pluralizeLib(word, count, inclusive);
}

/**
 * Converts a camelCase or PascalCase field name to a human-readable label.
 * Handles special cases like acronyms (AI, NSR, V) and common abbreviations.
 *
 * @example
 * humanizeFieldName('TitleVPermitNumber') // 'Title V Permit Number'
 * humanizeFieldName('AINumber') // 'AI Number'
 * humanizeFieldName('docType') // 'Doc Type'
 * humanizeFieldName('NSRPermitNumber') // 'NSR Permit Number'
 */
export function humanizeFieldName(fieldName: string): string {
   if (!fieldName) {
      return '';
   }

   // Unwrap dotted names (e.g. "some.dotted.fieldName" → "fieldName")
   if (fieldName.includes('.')) {
      fieldName = fieldName.split('.').filter(Boolean).at(-1) ?? '';
   }

   // Known acronyms that should stay uppercase
   const acronyms = ['AI', 'NSR', 'ID', 'URL', 'PDF', 'CCR', 'TEMPO'];

   // Special case mappings for fields that need custom labels
   const specialCases: Record<string, string> = {
      docid: 'Document ID',
      docurl: 'Document URL',
      DocType: 'Document Type',
      SubType: 'Sub Type',
      filename: 'Filename',
      page_count: 'Page Count',
      access_level_order: 'Access Level',
   };

   // Check for special cases first
   if (specialCases[fieldName]) {
      return specialCases[fieldName];
   }

   // Handle snake_case: replace underscores with spaces and capitalize
   let result = fieldName.replace(/_/g, ' ');

   // Insert space before uppercase letters (for camelCase/PascalCase)
   // But keep consecutive uppercase letters together (acronyms)
   result = result.replace(/([a-z])([A-Z])/g, '$1 $2');

   // Handle cases where an acronym is followed by a word (e.g., "AINumber" -> "AI Number")
   result = result.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

   // Capitalize first letter of each word
   result = result
      .split(' ')
      .map((word) => {
         // Check if word is a known acronym (case-insensitive)
         const upperWord = word.toUpperCase();
         if (acronyms.includes(upperWord)) {
            return upperWord;
         }
         // Handle "TitleV" -> keep "V" uppercase
         if (word.length === 1) {
            return word.toUpperCase();
         }
         // Capitalize first letter, lowercase rest
         return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');

   // Fix specific patterns that might have been broken
   result = result.replace(/Title v/gi, 'Title V');

   return result;
}
